/**
 * spatial_math.ts — where a sound is, relative to the ears (2026-09-20). PURE.
 *
 * THE LISTENER NEVER MOVES. Web Audio's `AudioListener` position/orientation
 * AudioParams are not implemented everywhere (Firefox still tracks them in an
 * open bug, and MDN marks `forwardX` limited availability), and the old
 * setters are deprecated. So this runtime leaves the native listener at its
 * default pose — at the origin, looking down −Z, +Y up — on EVERY browser and
 * moves the WORLD instead: each emitter is expressed in listener-local
 * coordinates and written to its PannerNode (whose AudioParams are universal).
 * One code path, identical on every engine, and it fixes two older bugs at once:
 *
 *   - the zone listener never TURNED (only `setListenerPosition` was called, so
 *     a hum stayed in the same ear when you turned around), and
 *   - 32-bit AudioParams held ABSOLUTE world coordinates — at 350 km from the
 *     origin a float32 resolves ~3 cm, and the FP64 rebase lives elsewhere.
 *     The subtraction happens here, in doubles; the panner only ever sees a
 *     small relative vector.
 *
 * Conventions: three.js — a camera looks down its local −Z, +Y up; a body whose
 * `rotation.y = yaw` faces (−sin yaw, 0, −cos yaw).
 */

export type Vec3 = [number, number, number];
/** Quaternion (x, y, z, w), unit length. */
export type Quat = [number, number, number, number];

export interface ListeningPose {
  /** ABSOLUTE world position of the ears (metres). */
  x: number; y: number; z: number;
  /** World orientation of the head. */
  q: Quat;
}

export const IDENTITY_POSE: ListeningPose = { x: 0, y: 0, z: 0, q: [0, 0, 0, 1] };

/** Rotate `v` by the INVERSE of unit quaternion `q` (world → listener-local). */
export function rotateByInverse(out: Vec3, v: Vec3, q: Quat): Vec3 {
  // v' = q* · v · q   (conjugate = inverse for a unit quaternion)
  const qx = -q[0], qy = -q[1], qz = -q[2], qw = q[3];
  const vx = v[0], vy = v[1], vz = v[2];
  // t = 2 · (q.xyz × v)
  const tx = 2 * (qy * vz - qz * vy), ty = 2 * (qz * vx - qx * vz), tz = 2 * (qx * vy - qy * vx);
  out[0] = vx + qw * tx + (qy * tz - qz * ty);
  out[1] = vy + qw * ty + (qz * tx - qx * tz);
  out[2] = vz + qw * tz + (qx * ty - qy * tx);
  return out;
}

/** An emitter's position in LISTENER-LOCAL space: −Z ahead, +X right, +Y up. */
export function toListenerSpace(out: Vec3, sx: number, sy: number, sz: number, pose: ListeningPose): Vec3 {
  out[0] = sx - pose.x; out[1] = sy - pose.y; out[2] = sz - pose.z; // doubles: exact at any distance from the origin
  return rotateByInverse(out, out, pose.q);
}

/** Yaw-only head orientation (rotation about +Y) — the fallback when no camera is known. */
export function quatFromYaw(yaw: number): Quat { return [0, Math.sin(yaw / 2), 0, Math.cos(yaw / 2)]; }

/** Normalise in place; a degenerate quaternion becomes identity (never NaN into an AudioParam). */
export function normalizeQuat(q: Quat): Quat {
  const n = Math.hypot(q[0], q[1], q[2], q[3]);
  if (!(n > 1e-9) || !Number.isFinite(n)) { q[0] = 0; q[1] = 0; q[2] = 0; q[3] = 1; return q; }
  q[0] /= n; q[1] /= n; q[2] /= n; q[3] /= n;
  return q;
}

/**
 * A panner at EXACTLY the listener has no direction, and engines disagree on
 * what that sounds like. Keep a source at least this far out (its direction is
 * preserved; a source truly at the ears is placed just ahead).
 */
export const MIN_PAN_DISTANCE_M = 0.25;
export function clampNearField(v: Vec3): Vec3 {
  const d = Math.hypot(v[0], v[1], v[2]);
  if (d >= MIN_PAN_DISTANCE_M) return v;
  if (d < 1e-6) { v[0] = 0; v[1] = 0; v[2] = -MIN_PAN_DISTANCE_M; return v; }
  const k = MIN_PAN_DISTANCE_M / d;
  v[0] *= k; v[1] *= k; v[2] *= k;
  return v;
}

/**
 * AIR ABSORPTION as a low-pass cutoff (Hz) by distance — highs die first over
 * distance, which is most of why a far shot sounds FAR rather than merely
 * quiet. An authored curve, not a calibrated atmosphere, and deliberately
 * GENTLE: every gain in the game was tuned by ear against full-bandwidth
 * playback, and a darker sound reads as a quieter one. Wide open inside 25 m,
 * ~8 kHz by 150 m, ~4 kHz by 600 m, never below 2.5 kHz.
 */
export function airAbsorptionHz(distM: number): number {
  const d = Math.max(0, distM);
  if (d <= 25) return 20000;
  const hz = 20000 * Math.sqrt(25 / d);
  return Math.max(2500, Math.min(20000, hz));
}

/** Extra low-pass + gain for a blocked direct path (0 = clear … 1 = fully blocked). Finite: a wall muffles, it never erases. */
export function obstructionTarget(blocked: number): { gain: number; cutoffHz: number } {
  const b = Math.min(1, Math.max(0, blocked));
  return { gain: 1 - 0.55 * b, cutoffHz: 20000 * Math.pow(0.055, b) }; // fully blocked: −7 dB, ~1.1 kHz
}

/**
 * A distance gain for a call site that never had one (a convoy crash used to play at full level from five kilometres):
 * full inside `refM`, inverse-distance beyond (−6 dB per doubling — a point source in free field), eased to zero over
 * the last fifth of `maxM` so the cutoff is never a step. The CALLER multiplies its gain by this — it stays the one
 * distance owner; the panner adds direction only.
 */
export function inverseDistanceGain(distM: number, refM: number, maxM: number): number {
  const d = Math.max(0, distM);
  if (!(maxM > 0) || d >= maxM) return 0;
  const ref = Math.max(0.01, refM);
  const inv = ref / Math.max(ref, d);
  const edge = maxM * 0.8;
  return d <= edge ? inv : inv * (1 - (d - edge) / (maxM - edge));
}

/** Seconds for sound to arrive through air (343 m/s — a temperate default, configurable per world later). */
export const SPEED_OF_SOUND_MPS = 343;
export function arrivalDelayS(distM: number): number { return Math.max(0, distM) / SPEED_OF_SOUND_MPS; }

const OCTANTS = ['ahead', 'ahead right', 'right', 'behind right', 'behind', 'behind left', 'left', 'ahead left'] as const;
/** Where a listener-local vector lies, in words (captions). */
export function bearingLabel(local: Vec3): string {
  const ang = Math.atan2(local[0], -local[2]); // 0 = ahead, +π/2 = right
  const i = ((Math.round(ang / (Math.PI / 4)) % 8) + 8) % 8;
  return OCTANTS[i];
}
